/* Vision Attire custom builder: real photographs and chart-based colour choices,
   plus Fully Custom, a guided enquiry for the customer's own artwork (client, 14 Sep 2026). */
(() => {
  'use strict';

  const root = document.getElementById('cfg');
  // Analytics (vaTrack is defined in the page head). builder_start fires once,
  // on the first real touch of the builder, not on page load.
  const vaSend = (n, p) => { if (window.vaTrack) window.vaTrack(n, p || {}); };
  if (root) {
    const start = e => {
      if (!e.target.closest('button, input, select, textarea, label')) return;
      root.removeEventListener('click', start); root.removeEventListener('change', start);
      vaSend('builder_start', { style: state.style, mode: state.mode });
    };
    root.addEventListener('click', start); root.addEventListener('change', start);
  }
  if (!root) return;

  const $  = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  const money = (n) => '$' + n.toFixed(2).replace(/\.00$/, '');
  const esc = (s) => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const inch = (n) => (Number(n) % 1 ? Number(n).toFixed(1) : Number(n)) + '&Prime;';
  const local = () => ['localhost', '127.0.0.1'].includes(location.hostname) || location.protocol === 'file:';

  const HEX6 = /^#[0-9a-f]{6}$/i;
  // Accepts "#abc", "abc", "#aabbcc", "aabbcc". Returns null on anything else,
  // so a half-typed hex never reaches the preview.
  const normHex = (v) => {
    let s = String(v || '').trim();
    if (s && s[0] !== '#') s = '#' + s;
    if (/^#[0-9a-f]{3}$/i.test(s)) s = '#' + s[1] + s[1] + s[2] + s[2] + s[3] + s[3];
    return HEX6.test(s) ? s.toUpperCase() : null;
  };
  // Netlify Forms takes 8 MB per submission, but the Shopify relay (netlify/functions/form.mjs) caps it at 5.5 MB.
  // One file per field. Leave room for the other fields.
  const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
  const SHOPIFY = window.VA_FORMS === 'shopify';

  let C = null, S = null, ZONES = [];
  let canvas = null, renderRequest = 0;   // photographic preview, once it has loaded
  let transfer, imported = false;
  let paletteNode = null, chartHost = null;
  let chartUI, referenceControl, gradientControl, optionsControl;
  const viewName = v => ({front:"Front",back:"Back","side-1":"Side 1","side-2":"Side 2"})[v] || "Front";

  const designs = {};
  const state = {
    mode: 'builder',            // 'builder' | 'fullyCustom'
    style: 'ombre',
    colours: {},
    chartReference: '',
    laces: '#101014',
    waist: null,
    length: 14,
    lengthTouched: false,
    original: false,
    sideTouched: false,
    area: 'body',
    extras: { waistbandText: false, stripes: false, rush: false },
    waistbandText: '',
    view: 'front',
    preferred: [],              // Fully Custom preferred colours, chart hex values
  };
  const fully = () => state.mode === 'fullyCustom';

  Promise.all([
    fetch(VA_DATA('assets/data/colours.json')).then(r => r.json()),
    fetch(VA_DATA('assets/data/site.json')).then(r => r.json()),
  ]).then(([colours, site]) => {
    C = colours; S = site;
    ZONES = C.zones;
    validateChart();
    chartUI = VA_CHART(C);
    resetColours();
    transfer = VA_DESIGN(C.styles.options, C.swatches, C.laces.options);
    const saved = transfer.read();
    if (saved.value) { applyDesign(saved.value); imported = true; }
    const params = new URLSearchParams(location.search);
    if (params.get('mode') === 'fully-custom' || location.hash === '#fully-custom') state.mode = 'fullyCustom';
    boot();
    if (saved.invalid) {
      const warning = document.createElement('p'); warning.className = 'note note--warn'; warning.setAttribute('role', 'alert');
      warning.textContent = 'The saved design could not be read. Please check your colours before sending.';
      root.prepend(warning);
    }
  }).catch(error => {
    console.error('Builder setup failed', error);
    root.innerHTML = '<p class="note note--warn">Could not load the colour chart. Refresh, or call us and we\'ll take the spec over the phone.</p>';
  });

  const lacesById = (v) => C.laces.options.find(l => l.id === v)
    || C.laces.options.find(l => l.hex.toUpperCase() === String(v).toUpperCase())
    || C.laces.options[0];
  const styleById = (id) => C.styles.options.find(s => s.id === id) || C.styles.options[0];
  const price = () => S.pricing || {customBuilder: 200, fullyCustomFrom: 350};

  const zoneLabel = (z) => z.id === 'side' ? styleById(state.style).sideLabel : z.label;
  // The outline only exists when the logo is outlined.
  const activeZones = () => styleById(state.style).zones.map(id => ZONES.find(z => z.id === id))
    .filter(z => z && (z.id !== 'logoOutline' || VA_PARTS.outlined(state.colours)));
  function validateChart() {
    if (!C.families?.length || !C.swatches?.length || !C.zones?.length) throw new Error('Incomplete colour chart');
    const families=new Set(C.families.map(f=>f.id));
    for(const sw of C.swatches) if(!HEX6.test(sw.hex)||!families.has(sw.family)||!sw.id||!sw.name) throw new Error('Invalid colour chart entry');
    const allowed=new Set(C.swatches.map(sw=>sw.hex.toUpperCase()));
    for(const st of C.styles.options) for(const hex of Object.values(st.defaultColours)) if(!allowed.has(hex.toUpperCase())) throw new Error('Style colour missing from chart');
  }
  function resetColours() {
    const st = styleById(state.style);
    state.colours = {...st.defaultColours, ...structuredClone(st.defaultOptions || {})};
    state.laces = lacesById(st.defaultLaces).hex;
    state.original = false;
  }
  function designPayload() {
    const all = {};
    for (const st of C.styles.options) {
      const saved = designs[st.id];
      all[st.id] = st.id === state.style ? {...state.colours, laces: state.laces}
        : saved ? {...saved.colours, laces: saved.laces}
        : {...st.defaultColours, ...(st.defaultOptions || {}), laces: lacesById(st.defaultLaces).hex};
    }
    return {v: 1, style: state.style, designs: all, chartReference: state.chartReference};
  }
  function applyDesign(input) {
    const value = transfer.normalise(input);
    if (!value) return false;
    for (const [id, colours] of Object.entries(value.designs)) {
      const {laces, ...printed} = colours;
      designs[id] = {colours: printed, laces, sideTouched: true};
    }
    state.mode = 'builder';
    state.style = value.style;
    state.chartReference = value.chartReference;
    if (referenceControl) referenceControl.set(state.chartReference);
    state.colours = {...designs[state.style].colours};
    state.laces = designs[state.style].laces;
    state.area = 'body'; state.original = false; state.sideTouched = true;
    transfer.save(value);
    return true;
  }
  // One function syncs every control to the state, so nothing can drift.
  function sampleControls() {
    if (!$('[data-regions]')) return;
    const st = styleById(state.style);

    $$('[data-style]').forEach(b => b.setAttribute('aria-pressed', String(!fully() && b.dataset.style === state.style)));
    $('[data-mode-fully]').setAttribute('aria-pressed', String(fully()));
    const field = $('[data-stylefield]'); if (field) field.value = fully() ? 'Fully Custom' : state.style;
    $('[data-modefield]').value = fully() ? 'fully-custom' : 'builder';
    $('[data-style-title]').textContent = fully() ? C.fullyCustom.name : st.name;
    $('.bldstage__sub').textContent = fully() ? 'Your artwork. Your design.' : 'Your colours. Every detail.';
    $$('[data-view]').forEach(b => {b.setAttribute('aria-pressed', String(b.dataset.view === state.view));});
    syncMode();
    if (fully()) { syncFully(); return; }
    if(gradientControl)gradientControl();

    const original = $('[data-original]');
    original.setAttribute('aria-pressed', String(state.original));
    original.textContent = state.original ? 'Return to your design' : 'View original sample';
    $('[data-preview-label]').textContent =
      (state.original ? 'Original sample' : 'Your design') + ' · ' + viewName(state.view);

    const list = areas();
    if ($$('.bldregion').length !== list.length
        || $$('.bldregion').some((b, i) => b.dataset.area !== list[i][0])) buildRegions();
    if (!list.some(a => a[0] === state.area)) state.area = 'body';
    $$('.bldregion').forEach(b => {
      const id = b.dataset.area;
      const label = (list.find(a => a[0] === id) || [])[1];
      b.setAttribute('aria-pressed', String(id === state.area));
      b.setAttribute('aria-expanded', String(id === state.area));
      b.setAttribute('aria-controls', 'cfgpalette');
      if (label) b.querySelector('.bldregion__label').textContent = label;
      b.querySelector('.bldregion__dot').style.background = areaSwatch(id);
      b.querySelector('.bldregion__colour').textContent = swatchName(areaColour(id), id);
    });

    $(`.bldregion[data-area="${state.area}"]`).after(paletteNode);
    $('[data-area-heading]').textContent = (list.find(a => a[0] === state.area) || ['', ''])[1];
    $('[data-current-colour]').textContent = swatchName(areaColour(state.area), state.area);
    // The drawstring is a stocked part, so its two colours are the whole list.
    $('[data-family-wrap]').hidden = state.area === 'laces';
    if (optionsControl) optionsControl();
    buildSwatches();

    const canvas = $('[data-stage] canvas');
    if (canvas) canvas.setAttribute('aria-label',
      viewName(state.view) + ' view of '
      + (state.original ? 'the original sample' : 'your ' + st.name + ' shorts'));
  }

  // Everything that differs between the colour builder and Fully Custom.
  function syncMode() {
    const f = fully();
    $('.bld').classList.toggle('bld--fully', f);
    $('[data-builder-console]').hidden = f;
    $('[data-fully-console]').hidden = !f;
    (f ? $('[data-fc-reference]') : $('[data-chart-reference-slot]')).append(chartHost);
    $('[data-original]').hidden = f;
    $('[data-save]').hidden = f;
    $('[data-fc-brief]').hidden = !f;
    $$('[data-fc-brief] input, [data-fc-brief] textarea').forEach(el => { el.disabled = !f; });
    $('#fcidea').required = f;
    $('[data-extras]').hidden = f;
    if (f) {
      $$('[name=extra]').forEach(el => { if (el.checked) { el.checked = false; el.dispatchEvent(new Event('change', {bubbles: true})); } });
      $('[data-ordertype]').value = 'custom';
    }
    $('#cfgform [type=submit]').textContent = local() ? 'Download specification' : f ? 'Send for review and quote' : 'Send design request';
    const open3d = $('[data-open-3d]'); if (open3d) open3d.hidden = f;
    const intro = $('[data-builder-lede]');
    if (intro) intro.textContent = f
      ? 'Send your own artwork, logos, patterns or reference images. We review every Fully Custom request and quote it before anything is made.'
      : 'Choose your style, colour each part and build your specification. Your final design is confirmed on a mockup before production.';
    const copyright = $('[data-copyright]'); copyright.hidden = !(f && hasFiles());
  }

  function syncFully() {
    const max = C.fullyCustom.maxPreferredColours;
    $('[data-preview-label]').textContent = 'Example Fully Custom pair · ' + viewName(state.view);
    $('[data-fc-count]').textContent = state.preferred.length ? state.preferred.length + ' of ' + max + ' chosen' : 'Choose up to ' + max;
    $('[data-fc-chosen]').replaceChildren(...state.preferred.map(hex => {
      const row = document.createElement('div'); row.className = 'fc-chosen__row';
      const dot = document.createElement('span'); dot.className = 'bldregion__dot'; dot.style.background = hex; dot.setAttribute('aria-hidden', 'true');
      const name = document.createElement('span'); name.textContent = swatchName(hex, 'fc');
      const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'fc-chosen__remove';
      remove.textContent = 'Remove'; remove.setAttribute('aria-label', 'Remove ' + swatchName(hex, 'fc'));
      remove.onclick = () => { state.preferred = state.preferred.filter(h => h !== hex); sampleControls(); paintChrome(); };
      row.append(dot, name, remove); return row;
    }));
    const fam = $('[data-fc-family]');
    $('[data-fc-swatches]').replaceChildren(...chartUI.shortlist.filter(s => s.family === fam.value).map(s => {
      const b = swatchButton(s, state.preferred.includes(s.hex));
      b.disabled = !state.preferred.includes(s.hex) && state.preferred.length >= max;
      b.addEventListener('click', () => {
        state.preferred = state.preferred.includes(s.hex) ? state.preferred.filter(h => h !== s.hex) : [...state.preferred, s.hex].slice(0, max);
        sampleControls(); paintChrome();
        $(`[data-fc-swatches] [data-hex="${s.hex}"]`)?.focus({preventScroll: true});
      });
      return b;
    }));
    const canvas = $('[data-stage] canvas');
    if (canvas) canvas.setAttribute('aria-label', viewName(state.view) + ' view of an example Fully Custom pair of shorts');
  }

  /* Which length we'd put this waist in. Inference, flagged in the data. */
  const standardFor = () => null; // Unconfirmed waist-to-length pairings must not recommend a fit.

  /* --- markup ----------------------------------------------------------- */
  function boot() {
    const lengthOpts = S.sizing.lengthOptions.map(l => {
      const ready = S.sizing.readyMadeLengths.includes(l);
      return `<option value="${l}" ${l === 14 ? 'selected' : ''}>${l}″${ready ? ' — ready-made length' : ''}</option>`;
    }).join('');
    const federations = [...S.federations.approved.filter(f => f !== 'Independent shows'), 'Independent show', 'Other', 'Not sure yet'];

    root.innerHTML = `
    <div class="bld">
      <nav class="bldnav" aria-label="Shorts style">
        ${C.styles.options.map(s => `
          <button type="button" data-style="${s.id}" aria-pressed="${s.id === state.style}">${esc(s.name)}</button>`).join('')}
          <button type="button" data-mode-fully aria-pressed="false">${esc(C.fullyCustom.name)}</button>
      </nav>

      <div class="bldwork">
        <section class="bldstage" aria-label="Shorts preview">
          <div class="bldstage__top">
            <div>
              <h2 class="bldstage__title" data-style-title>${esc(styleById(state.style).name)}</h2>
              <p class="bldstage__sub">Your colours. Every detail.</p>
            </div>
            <div class="bldviews" role="group" aria-label="Change view">
              <button type="button" data-view="front" aria-pressed="true">Front</button>
              <button type="button" data-view="back" aria-pressed="false">Back</button>
              <button type="button" data-view="side-1" aria-pressed="false">Side 1</button>
              <button type="button" data-view="side-2" aria-pressed="false">Side 2</button>
            </div>
          </div>
          <div class="bldphoto" data-stage aria-busy="true"></div>
          <div class="bldstage__bot">
            <span data-preview-label>Your design &middot; Front</span>
            <span class="bldstage__price"><b data-price>${money(price().customBuilder)}</b><span data-depline></span></span>
            <button type="button" class="bldsample" data-original aria-pressed="false">View original sample</button>
          </div>
        </section>

        <aside class="bldconsole">
          <div data-builder-console>
            <h2 class="bldconsole__h">Choose your colours</h2>
            <p class="bldconsole__intro">Select a part below or on the shorts.</p>
            <div class="bldregions" data-regions></div>
            <div class="bldpalette" id="cfgpalette" role="region" aria-label="Choose a colour">
              <div class="bldpalette__head">
                <h3 data-area-heading></h3>
                <span class="bldpalette__now" data-current-colour></span>
              </div>
              <div data-options></div>
              <div data-family-wrap>
                <label class="bldpalette__lbl" for="cfgfamily">Colour family</label>
                <select class="input" id="cfgfamily" data-family></select>
              </div>
              <div class="bldswatches" data-swatches></div>
              <p class="bldhelp">${esc(C._meta.shortlistNote)} ${esc(C._meta.screenDisclaimer)}</p>
            </div>
            <div data-chart-reference-slot></div>
          </div>

          <div data-fully-console hidden>
            <h2 class="bldconsole__h">Tell us your design</h2>
            <p class="bldconsole__intro">${esc(C.fullyCustom.desc)} From ${money(price().fullyCustomFrom)}, depending on the artwork, pattern and design.</p>
            <div class="fc-colours" role="group" aria-labelledby="fccolours">
              <p class="fc-colours__head"><span id="fccolours">Preferred colours</span><span data-fc-count></span></p>
              <div class="fc-chosen" data-fc-chosen></div>
              <label class="bldpalette__lbl" for="fcfamily">Colour family</label>
              <select class="input" id="fcfamily" data-fc-family></select>
              <div class="bldswatches" data-fc-swatches></div>
              <p class="bldhelp">${esc(C._meta.screenDisclaimer)}</p>
            </div>
            <div data-fc-reference></div>
            <p class="bldhelp">Add your files and describe the idea in the brief below.</p>
          </div>

          <div data-chart-reference></div>
          <div class="bldactions">
            <button type="button" class="bldsave" data-save disabled>Save design image</button>
            <button type="button" class="bldreset" data-reset>Reset</button>
          </div>
          <p class="bldstatus" data-preview-status role="status">Loading your preview&hellip;</p>
        </aside>
      </div>

      <!-- multipart is required or the artwork upload silently never arrives. -->
      <form class="bld__form" id="cfgform" data-confirm="Design request received. Vision Attire will confirm the design and next steps."
            name="custom-order" method="POST" enctype="multipart/form-data"
            data-netlify="true" netlify-honeypot="bot-field">
        <input type="hidden" name="form-name" value="custom-order">
        <p hidden><label>Leave blank <input name="bot-field"></label></p>
        <input type="hidden" name="spec" data-spec>
        <input type="hidden" name="designMode" value="builder" data-modefield>
        <input type="hidden" name="referralCode"><input type="hidden" name="referralSource"><input type="hidden" name="referralLanding">

        <section class="bld__sec" data-fc-brief hidden>
          <h3>Your design brief</h3>
          <p class="hint">${SHOPIFY ? 'Tell us what you want, then email us your artwork, logos, patterns or reference images.' : 'Upload artwork, logos, patterns or reference images, then tell us what you want.'} Fully Custom designs may not match exactly on screen, so we confirm everything with you before anything is made.</p>
          <div class="field">
            <label class="lbl" for="artwork">Artwork or reference image</label>
            <input class="input" type="file" id="artwork" name="artwork" accept="image/*,application/pdf" disabled data-fc-file>
          </div>
          <div class="field">
            <label class="lbl" for="artwork2">Second file <span class="hint">(optional)</span></label>
            <input class="input" type="file" id="artwork2" name="artwork2" accept="image/*,application/pdf" disabled data-fc-file>
          </div>
          <div class="field">
            <label class="lbl" for="artwork3">Third file <span class="hint">(optional)</span></label>
            <input class="input" type="file" id="artwork3" name="artwork3" accept="image/*,application/pdf" disabled data-fc-file>
            <p class="hint mt-1">Images or PDF, up to 5 MB in total. High resolution please, because it's printed onto fabric.</p>
            <p class="err" data-err-files>Your files add up to more than 5 MB. Please send smaller versions, and we'll ask for full-size files once we've reviewed your design.</p>
          </div>
          <div class="field">
            <label class="lbl" for="fcidea">Describe your design <span aria-hidden="true" style="color:var(--acc-tx)">*</span></label>
            <textarea class="input" id="fcidea" name="designIdea" maxlength="2000" disabled placeholder="The idea, the colours you want, what it should look like on stage."></textarea>
          </div>
          <div class="field">
            <label class="lbl" for="fcplacement">Where should your logos or artwork go?</label>
            <textarea class="input" id="fcplacement" name="artworkPlacement" maxlength="1000" disabled placeholder="e.g. claw marks down the left leg, gym logo on the back waistband."></textarea>
          </div>
        </section>

        <section class="bld__sec">
          <h3>Fit</h3>
          <div class="field">
            <label class="lbl" for="cfgwaist">Waist size <span aria-hidden="true" style="color:var(--acc-tx)">*</span></label>
            <select class="input" id="cfgwaist" name="waist">
              <option value="">Choose your waist…</option>
              ${S.sizing.waistSizes.map(w => `<option value="${w}">${w}″</option>`).join('')}
            </select>
            <p class="err" data-err-waist>Pick a waist size to continue.</p>
            <p class="hint mt-1">${esc(S.sizing.oddSizesNote)} Not sure? <a href="${VA_PAGE('sizing.html')}" style="color:var(--acc-tx)">Work it out in 30 seconds</a>.</p>
          </div>
          <div class="field">
            <label class="lbl" for="cfglength">Length</label>
            <select class="input" id="cfglength" name="length">${lengthOpts}</select>
            <p class="hint mt-1" data-lenhint>Lengths come in whole inches. Length is recorded in your specification; the photograph keeps the sample’s actual proportions.</p>
          </div>
        </section>

        <!-- Style and colour live in the builder above; the form carries the
             rest of the order. The style still has to reach the payload, so it
             rides along as a hidden input kept in step with the tabs. -->
        <input type="hidden" name="style" value="${state.style}" data-stylefield>

        <section class="bld__sec" data-extras>
          <h3>Extras</h3>
          <p class="hint">Additional side stripes and inside-waistband text are recorded for your approval mockup.</p>
          ${C.extras.options.map(x => `
            <label class="check check--box">
              <input type="checkbox" name="extra" value="${x.id}">
              <span><b style="color:var(--ink)">${esc(x.name)} &middot; +${money(x.price)}</b><br>${esc(x.desc)}</span>
            </label>`).join('')}
          <div class="field mt-1" data-wbtext hidden>
            <label class="lbl" for="wbtext">What should it say?</label>
            <input class="input" type="text" id="wbtext" name="waistbandText" maxlength="${C.extras.options[0].maxChars}" placeholder="e.g. JEROME — IFBB 2026">
            <p class="hint mt-1">Up to ${C.extras.options[0].maxChars} characters.</p>
          </div>
        </section>

        <section class="bld__sec">
          <h3>Your show</h3>
          <p class="hint">Tell us your federation and show date and we'll say honestly whether this lands in time.</p>
          <div class="field">
            <label class="lbl" for="cfgfederation">Federation</label>
            <select class="input" id="cfgfederation" name="federation">
              <option value="">Choose your federation…</option>
              ${federations.map(f => `<option value="${esc(f)}">${esc(f)}</option>`).join('')}
            </select>
          </div>
          <div data-calc>
            <div class="field">
              <label class="lbl" for="cfgdate">Show date</label>
              <input class="input" type="date" id="cfgdate" name="showDate">
            </div>
            <input type="hidden" name="orderType" value="custom" data-ordertype>
            <div class="field">
              <label class="lbl" for="cfgregion">Delivering to</label>
              <select class="input" id="cfgregion" name="region">
                <option value="AU-standard">Australia — standard</option>
                <option value="AU-express">Australia — express</option>
                <option value="New Zealand">New Zealand</option>
                <option value="United States">United States</option>
                <option value="United Kingdom &amp; Europe">United Kingdom &amp; Europe</option>
                <option value="Rest of world">Rest of world</option>
              </select>
            </div>
            <div class="calc__out" data-calc-out hidden></div>
          </div>
        </section>

        <section class="bld__sec">
          <h3>Before you send</h3>
          <p class="hint">These matter, so they're here rather than buried in the terms.</p>
          <label class="check check--box">
            <input type="checkbox" name="okFederation">
            <span>${esc(C.disclaimers.federation)}</span>
          </label>
          <label class="check check--box" data-copyright hidden>
            <input type="checkbox" name="okCopyright">
            <span>${esc(SHOPIFY ? C.disclaimers.copyright.replace('I upload', 'I send') : C.disclaimers.copyright)}</span>
          </label>
          <p class="err" data-err-terms>Please confirm the federation note before you continue.</p>
        </section>

        <section class="bld__sec">
          <h3>Your build</h3>
          <p class="hint" data-design-summary aria-live="polite"></p>
          <div class="summary" data-summary></div>
          <div class="field mt-2">
            <label class="lbl" for="cfgname">Your name</label>
            <input class="input" type="text" id="cfgname" name="name" required autocomplete="name">
          </div>
          <div class="field">
            <label class="lbl" for="cfgemail">Email</label>
            <input class="input" type="email" id="cfgemail" name="email" required autocomplete="email">
          </div>
          <div class="field">
            <label class="lbl" for="cfgnotes">Anything else we should know?</label>
            <textarea class="input" id="cfgnotes" name="notes" placeholder="Division, what you're going for."></textarea>
          </div>
          <button class="btn btn--wide" type="submit">Send design request</button>
          <p class="hint mt-1" data-submit-note>You approve a mockup before anything is made. ${esc(S.deposit.refundPolicy)}</p>
        </section>
      </form>
    </div>`;

    // On a phone the stage pins itself to the top of the viewport, so anything
    // scrolled to underneath it has to clear that height or it lands behind it.
    const stage = $('.bldstage');
    new ResizeObserver(() => document.documentElement.style
      .setProperty('--builder-stage-height', stage.offsetHeight + 'px')).observe(stage);
    paletteNode = $('.bldpalette');
    const gradientHost=document.createElement('div');$('[data-regions]').before(gradientHost);
    gradientControl=VA_OMBRE.mount(gradientHost,()=>({colours:state.colours,area:state.area,ombre:state.style==='ombre',disabled:false}),area=>{state.area=area;state.original=false;sampleControls();paint();});
    optionsControl = VA_OPTIONS.mount($('[data-options]'), () => ({style: state.style, colours: state.colours, area: state.area}), optionChanged);
    chartHost = $('[data-chart-reference]');
    referenceControl = chartUI.mount(chartHost, state.chartReference, value => {state.chartReference=value; transfer.save(designPayload()); paintChrome();});
    buildFamilies();
    $('[data-family]').value = chartUI.swatch(state.colours.body)?.family || C.families[0].id;
    buildRegions();
    wire();
    sampleControls();
    paint();
    wire3D();
    if (new URLSearchParams(location.hash.slice(1)).has('design')) {
      history.replaceState(null, '', location.pathname + location.search + '#cfgform');
      if (imported) requestAnimationFrame(() => $('#cfgform').scrollIntoView({block: 'start'}));
    }
  }

  function optionChanged(key) {
    const c = state.colours;
    if (key === 'stripe-add') state.area = 'stripe' + VA_STRIPE.colours(c).length;
    if (key === 'logo-solid' && state.area === 'logoOutline') state.area = 'logoLeft';
    if (!areas().some(a => a[0] === state.area)) state.area = key.startsWith('stripe') ? 'side' : 'body';
    if (key.startsWith('stripe') || key.startsWith('logo')) state.view = 'front';
    state.original = false;
    sampleControls();
    paint();
  }

  function wire3D() {
    const open = $('[data-open-3d]');
    if (!open) return;
    const dialog = document.createElement('dialog');
    dialog.className = 'builder-3d-dialog';
    dialog.setAttribute('aria-label', 'Edit your shorts in 3D');
    dialog.innerHTML = '<div class="builder-3d-dialog__bar"><span>Edit your design</span><button type="button" class="btn btn--ghost btn--sm" data-close-3d>Back to details</button></div><iframe title="Vision Attire 3D shorts builder" allow="xr-spatial-tracking" src="about:blank"></iframe>';
    document.body.append(dialog);
    const frame = $('iframe', dialog);
    const close = () => { dialog.close(); frame.removeAttribute('srcdoc'); frame.src = 'about:blank'; document.body.style.overflow = ''; open.focus({preventScroll: true}); };
    $('[data-close-3d]', dialog).onclick = close;
    dialog.addEventListener('cancel', e => {e.preventDefault();close();});
    open.disabled = false;
    // On Shopify VA_BUILDER is the store's builder template, and a theme preview has to be named again for the frame.
    const builder = new URL(window.VA_BUILDER || 'builder-3d.html', location.href);
    builder.searchParams.set('embed', '1'); builder.searchParams.set('v', '13');
    if (builder.origin !== location.origin) builder.searchParams.set('parent', location.origin);
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) builder.searchParams.set('preview_theme_id', preview);
    // Shopify sends X-Frame-Options: DENY on every storefront page, the store's own included, so there the builder is
    // fetched and written into the frame, told the URL it stands for. If that fails it opens as a page of its own.
    let page;
    open.onclick = async () => {
      const url = builder.href + transfer.fragment(designPayload());
      if (!window.VA_BUILDER) frame.src = url;
      else try {
        page ||= fetch(builder).then(r => r.ok ? r.text() : Promise.reject(r.status));
        const html = await page;
        frame.srcdoc = html.replace('<head>', '<head><script>window.VA_PAGE_URL=' + JSON.stringify(url).replace(/</g, '\\u003c') + '</script>');
      } catch {
        page = null;
        const own = new URL(url); own.searchParams.delete('embed'); location.assign(own);
        return;
      }
      dialog.showModal(); document.body.style.overflow = 'hidden';
    };
    window.addEventListener('message', e => {
      if (e.origin !== builder.origin || e.source !== frame.contentWindow || !dialog.open) return;
      if (e.data?.type === 'va-fully-custom') { close(); setMode('fullyCustom'); $('#cfgform').scrollIntoView({block: 'start'}); return; }
      if (!['va-design-change', 'va-design-continue'].includes(e.data?.type)) return;
      if (!applyDesign(e.data.value)) return;
      sampleControls(); paint();
      if (e.data.type === 'va-design-continue') {
        close(); $('#cfgform').scrollIntoView({block: 'start'}); $('#cfgwaist').focus({preventScroll: true});
      }
    });
  }

  /* --- the colour console ------------------------------------------------ */
  // Areas are the active zones for this style plus the laces, which are a
  // stocked component rather than printed fabric and so carry a fixed list.
  function areas() {
    const parts = activeZones().flatMap(z => {
      if (state.style === 'ombre' && z.id === 'body') return VA_OMBRE.areas(state.colours);
      if (state.style === 'ombre' && z.id === 'side') return [];
      if (state.style === 'stripe' && z.id === 'side') return VA_STRIPE.areas(state.colours);
      return [[z.id, zoneLabel(z)]];
    });
    return [...parts, ['laces', 'Drawstring']];
  }

  const areaColour = (id) => id === 'laces' ? state.laces : VA_PARTS.get(state.style, state.colours, id);
  const panelsFade = (id) => id === 'panels' && VA_PARTS.fadePanels(state.style, state.colours);
  const areaSwatch = (id) => panelsFade(id) ? 'linear-gradient(' + VA_OMBRE.stops(state.colours).join(',') + ')' : areaColour(id);
  const swatchName = (hex, area) => panelsFade(area) ? 'Matches the fade'
    : (area === 'laces' && C.laces.options.find(l=>l.available && l.hex===hex)?.name) || (chartUI.swatch(hex) ? chartUI.label(chartUI.swatch(hex)) : C.laces.options.find(l=>l.available && l.hex===hex)?.name || 'Custom');

  function buildFamilies() {
    for (const sel of [$('[data-family]'), $('[data-fc-family]')])
      sel.replaceChildren(...C.families.filter(f=>chartUI.shortlist.some(s=>s.family===f.id)).map(f => {
        const o = document.createElement('option');
        o.value = f.id; o.textContent = f.name; return o;
      }));
  }

  function buildRegions() {
    if (paletteNode) root.append(paletteNode);
    $('[data-regions]').replaceChildren(...areas().map(([id, label]) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bldregion';
      b.dataset.area = id;
      b.setAttribute('aria-pressed', String(id === state.area));
      b.innerHTML = '<span class="bldregion__dot" aria-hidden="true"></span>'
        + '<span><span class="bldregion__label"></span>'
        + '<span class="bldregion__colour"></span></span>';
      b.querySelector('.bldregion__label').textContent = label;
      b.addEventListener('click', () => selectArea(id));
      return b;
    }));
  }

  function selectArea(id) {
    if (!areas().some(a => a[0] === id)) return;
    state.area = id;
    // Jump to the side the part is actually on, so a pick is never invisible.
    if (id === 'logoBand') state.view = 'back';
    if (['logoLeft', 'logoOutline', 'laces'].includes(id) || VA_STRIPE.index(id) > 0 || (state.style === 'stripe' && id === 'side')) state.view = 'front';
    state.original = false;
    const sw = C.swatches.find(s => s.hex.toUpperCase() === String(areaColour(id)).toUpperCase());
    $('[data-family]').value = sw ? sw.family : C.families[0].id;
    sampleControls();
    paint();
  }

  function swatchButton(s, pressed) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'bldswatch';
    b.dataset.hex = s.hex;
    b.style.setProperty('--sw', s.hex);
    const n = parseInt(s.hex.slice(1), 16);
    const lum = (n >> 16) * .299 + (n >> 8 & 255) * .587 + (n & 255) * .114;
    b.style.setProperty('--swink', lum > 145 ? '#111' : '#fff');
    b.setAttribute('aria-label', chartUI.label(s));
    b.title = chartUI.label(s);
    if (s.code) b.dataset.code = s.code;
    b.setAttribute('aria-pressed', String(pressed));
    return b;
  }

  function buildSwatches() {
    if (!$('[data-family]').value) $('[data-family]').selectedIndex = 0;
    const list = state.area === 'laces'
      ? chartUI.laces
      : chartUI.shortlist.filter(s => s.family === $('[data-family]').value);
    const current = panelsFade(state.area) ? null : String(areaColour(state.area)).toUpperCase();
    $('[data-swatches]').replaceChildren(...list.map(s => {
      const b = swatchButton(s, s.hex.toUpperCase() === current);
      b.addEventListener('click', () => setColour(state.area, s.hex));
      return b;
    }));
  }

  /* --- wiring ----------------------------------------------------------- */
  function setColour(zone, hex) {
    const h = normHex(hex);
    if (!h) return false;
    if (zone === 'laces') { state.laces = h; }
    else {
      if (zone === 'side') state.sideTouched = true;
      VA_PARTS.set(state.style, state.colours, zone, h);
    }
    state.original = false;
    if (zone === 'logoBand') state.view = 'back';
    if (zone === 'logoLeft' || zone === 'logoOutline' || zone === 'laces') state.view = 'front';
    sampleControls();
    paint();
    return true;
  }

  function setMode(mode) {
    if (state.mode === mode) return;
    if (mode === 'fullyCustom') designs[state.style] = { colours: structuredClone(state.colours), laces: state.laces, sideTouched: state.sideTouched };
    state.mode = mode;
    vaSend('builder_mode', { mode });
    state.original = false;
    if (mode === 'fullyCustom' && !$('[data-fc-family]').value) $('[data-fc-family]').selectedIndex = 0;
    const url = new URL(location.href);
    if (mode === 'fullyCustom') url.searchParams.set('mode', 'fully-custom'); else url.searchParams.delete('mode');
    if (url.hash === '#fully-custom') url.hash = '';
    history.replaceState(null, '', url);
    buildRegions();
    sampleControls();
    paint();
  }

  // Shopify's contact form can't take files, so artwork comes by email and the copyright line always shows.
  const hasFiles = () => SHOPIFY || $$('[data-fc-file]').some(el => el.files && el.files.length);
  const fileBytes = () => $$('[data-fc-file]').reduce((n, el) => n + (el.files?.[0]?.size || 0), 0);

  function wire() {
    const form = $('#cfgform');
    $('[data-original]').addEventListener('click', () => {
      state.original = !state.original; sampleControls(); paint();
    });
    $('[data-reset]').addEventListener('click', () => {
      if (fully()) { state.preferred = []; sampleControls(); paintChrome(); return; }
      delete designs[state.style];
      resetColours();
      state.original = false;
      state.area = 'body';
      sampleControls();
      paint();
    });
    $('[data-save]').addEventListener('click', saveDesign);

    $('[data-family]').addEventListener('change', buildSwatches);
    $('[data-fc-family]').addEventListener('change', () => sampleControls());

    // Each style keeps its own colours, so switching back and forth never
    // loses work. sideTouched rides along or a restored fade resets itself.
    $$('[data-style]').forEach(b => b.addEventListener('click', () => {
      if (b.dataset.style === state.style && !fully()) return;
      if (fully()) { state.mode = 'builder'; const url = new URL(location.href); url.searchParams.delete('mode'); history.replaceState(null, '', url); }
      else designs[state.style] = { colours: structuredClone(state.colours), laces: state.laces, sideTouched: state.sideTouched };
      state.style = b.dataset.style;
      vaSend('builder_style', { style: state.style });
      const saved = designs[state.style];
      if (saved) {
        state.colours = structuredClone(saved.colours);
        state.laces = saved.laces;
        state.sideTouched = !!saved.sideTouched;
      } else {
        resetColours();
      }
      state.original = false;
      if (!areas().some(a => a[0] === state.area)) state.area = 'body';
      buildRegions();
      sampleControls();
      paint();
      VA_PHOTO.prefetch(state.style, state.view === 'front' ? 'back' : 'front', state.colours.stripePlacement);
    }));
    $('[data-mode-fully]').addEventListener('click', () => setMode('fullyCustom'));

    $$('[data-view]').forEach(b => b.addEventListener('click', () => {
      state.view = b.dataset.view;
      sampleControls();
      paint();
    }));

    const waistEl = $('#cfgwaist'), lenEl = $('#cfglength');

    waistEl.addEventListener('change', () => {
      state.waist = waistEl.value ? +waistEl.value : null;
      $('[data-err-waist]').removeAttribute('data-show');
      paint();
    });

    lenEl.addEventListener('change', () => {
      state.length = +lenEl.value;
      state.lengthTouched = true;
      paint();
    });

    $$('[name=extra]').forEach(el => el.addEventListener('change', () => {
      state.extras[el.value] = el.checked;
      if (el.value === 'waistbandText') { $('[data-wbtext]').hidden = !el.checked; $('#wbtext').required=el.checked; }
      if (el.value === 'rush') {
        $('[data-ordertype]').value = el.checked ? 'rush' : 'custom';
        $('#cfgdate').dispatchEvent(new Event('change', { bubbles: true }));
      }
      paint();
    }));

    const wb = $('#wbtext');
    if (wb) wb.addEventListener('input', () => { wb.setCustomValidity(''); state.waistbandText = wb.value; paint(); });

    $$('[data-fc-file]').forEach(el => el.addEventListener('change', () => {
      $('[data-err-files]').toggleAttribute('data-show', fileBytes() > MAX_UPLOAD_BYTES);
      syncMode(); paintChrome();
    }));
    $$('#fcidea, #fcplacement, #cfgfederation').forEach(el => el.addEventListener('input', () => paintChrome()));

    form.addEventListener('submit', (e) => {
      let bad = false;
      if (!state.waist) {
        $('[data-err-waist]').setAttribute('data-show', '');
        bad = true;
      }
      $('#wbtext').setCustomValidity(!fully() && state.extras.waistbandText && !state.waistbandText.trim() ? 'Enter the text you want printed.' : '');
      if(!form.checkValidity()){e.preventDefault();e.stopImmediatePropagation();form.reportValidity();return;}
      if (fully() && fileBytes() > MAX_UPLOAD_BYTES) { $('[data-err-files]').setAttribute('data-show', ''); bad = true; }
      const needCopyright = fully() && hasFiles();
      const termsOk = $('input[name=okFederation]').checked &&
                      (!needCopyright || $('input[name=okCopyright]').checked);
      if (!termsOk) {
        $('[data-err-terms]').textContent = needCopyright && $('input[name=okFederation]').checked
          ? 'Please confirm the artwork is your own before you continue.'
          : 'Please confirm the federation note before you continue.';
        $('[data-err-terms]').setAttribute('data-show', '');
        bad = true;
      } else {
        $('[data-err-terms]').removeAttribute('data-show');
      }
      if (bad) {
        e.preventDefault();
        e.stopImmediatePropagation();
        (document.querySelector('.err[data-show]') || form).scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (window.VA_ATTRIBUTION) window.VA_ATTRIBUTION.apply(form);
      const specification={...spec(),createdAt:new Date().toISOString(),name:$('#cfgname').value.trim(),email:$('#cfgemail').value.trim(),notes:$('#cfgnotes').value.trim(),showDate:$('#cfgdate').value||null,region:$('#cfgregion').value,federation:$('#cfgfederation').value||null,attribution:window.VA_ATTRIBUTION?.get()||null};
      $('[data-spec]').value = JSON.stringify(specification, null, 2);
      if(local()) {
        e.preventDefault();e.stopImmediatePropagation();
        const a=document.createElement('a');a.download='vision-attire-design.json';
        const url=URL.createObjectURL(new Blob([JSON.stringify(specification,null,2)],{type:'application/json'}));
        a.href=url;a.click();setTimeout(()=>URL.revokeObjectURL(url),1000);
        let notice=form.querySelector('[data-local-result]');
        if(!notice){notice=document.createElement('p');notice.className='note';notice.dataset.localResult='';notice.setAttribute('role','status');form.append(notice);}
        notice.textContent='Specification downloaded. This is a local preview; nothing has been sent and no payment has been taken.';
      }
    });

    // vision.js wires these on load, but this markup is built after that has
    // already run. Without the re-init the show-date check inside the builder
    // never binds — it was dead in the Phase 1 build for exactly this reason.
    if (window.__vaWire) window.__vaWire(form);
    if (window.__vaInitCalc) window.__vaInitCalc($('[data-calc]'));
  }

  /* --- pricing ---------------------------------------------------------- */
  function lines() {
    const st = styleById(state.style);
    const out = [{ label: `Custom physique shorts — ${st.name}`, amount: price().customBuilder }];
    C.extras.options.forEach(x => {
      if (state.extras[x.id]) out.push({ label: x.name, amount: x.price });
    });
    return out;
  }

  const chartRef = hex => { const sw = chartUI.swatch(hex); return {code: sw?.code || null, name: sw ? chartUI.label(sw) : null, chartName: sw?.name || null, hex}; };

  function spec() {
    const common = {
      waist: state.waist ? state.waist + '"' : null,
      length: state.length + '"',
      requestedChartReference: state.chartReference.trim(),
      requestedChartMatches: chartUI.matches(state.chartReference).map(s=>({code:s.code,name:chartUI.label(s),hex:s.hex})),
    };
    if (fully()) return {
      designMode: 'Fully Custom',
      style: 'Fully Custom',
      preferredColours: state.preferred.map(chartRef),
      designIdea: $('#fcidea').value.trim(),
      artworkPlacement: $('#fcplacement').value.trim(),
      artworkFiles: $$('[data-fc-file]').map(el => el.files?.[0]?.name).filter(Boolean),
      priceFrom: price().fullyCustomFrom,
      pricing: 'Quoted after Vision Attire reviews the design.',
      consent: {federation: $('input[name=okFederation]').checked, copyrightRequired: hasFiles(), copyright: hasFiles() ? $('input[name=okCopyright]').checked : null},
      ...common,
      shortlistStatus: C._meta.shortlistStatus,
    };
    const c = state.colours, st = styleById(state.style);
    const zones = activeZones();
    return {
      designMode: 'Builder',
      style: st.name,
      ...(state.style==='ombre'?{gradientStops:VA_OMBRE.stops(c).map((hex,i,a)=>({position:i/(a.length-1),...chartRef(hex)})), sidePanels: c.panelMode === 'solid' ? 'Single colour' : 'Match the ombre fade'}:{}),
      ...(state.style==='stripe'?{stripe:{placement: c.stripePlacement === 'right' ? 'Right leg' : 'Left leg', colours: VA_STRIPE.colours(c).map(chartRef)}}:{}),
      logo: c.logoStyle === 'solid' ? 'Solid' : 'Outlined',
      colours: Object.fromEntries(zones.map(z => [z.id, c[z.id]])),
      colourLabels: Object.fromEntries(zones.map(z => [zoneLabel(z), panelsFade(z.id) ? 'Matches the fade' : c[z.id]])),
      colourReferences: Object.fromEntries(zones.map(z => { const sw=chartUI.swatch(c[z.id]); return [z.id,{id:sw?.id,name:sw?chartUI.label(sw):null,code:sw?.code||null,hex:c[z.id]}]; })),
      ...common,
      shortlistStatus: C._meta.shortlistStatus,
      previewSource: 'Shared photograph and 3D builder selections',
      paletteStatus: C._meta.status,
      consent: {federation: $('input[name=okFederation]').checked, copyrightRequired: false, copyright: null},
      laces: lacesById(state.laces).name,
      trim: 'Piping colour covers the side seams and leg openings (no separate hem binding).',
      standardLengthForWaist: standardFor(state.waist),
      extras: Object.entries(state.extras).filter(([, v]) => v).map(([k]) => k),
      waistbandText: state.extras.waistbandText ? state.waistbandText : null,
      total: lines().reduce((a, l) => a + l.amount, 0),
      note: 'Colours are the customer\'s chosen values. Match to the nearest stocked fabric before the mockup.',
    };
  }

  /* --- render ----------------------------------------------------------- */
  function paint() {
    const st=styleById(state.style), request=++renderRequest;
    const stage=$('[data-stage]'), status=$('[data-preview-status]');
    if(!canvas) {
      canvas=document.createElement('canvas'); canvas.className='garment'; canvas.setAttribute('role','img');
      canvas.addEventListener('click',e=>{
        if(state.original || fully()) return;
        const zone=VA_PHOTO.pick(canvas,e.clientX,e.clientY);
        if(!zone) return;
        selectArea(zone);
        const b=$(`.bldregion[data-area="${zone}"]`);
        if(b) b.focus({preventScroll:true});
      });
    }
    const renderStyle = fully() ? 'fullyCustom' : state.style;
    const key=renderStyle+'|'+state.view;
    if(canvas.dataset.rendered!==key) canvas.hidden=true;
    stage.replaceChildren(canvas);stage.setAttribute('aria-busy','true');
    $('[data-save]').disabled=true; status.textContent='Updating preview…';
    paintChrome();
    VA_PHOTO.render(canvas,{style:renderStyle,view:state.view,colours:state.colours,laces:state.laces,original:state.original})
      .then(ok=>{
        if(request!==renderRequest || !ok) return;
        canvas.hidden=false;stage.setAttribute('aria-busy','false');$('[data-save]').disabled=fully();
        canvas.setAttribute('aria-label',fully()?`${viewName(state.view)} view of an example Fully Custom pair of shorts`:`${viewName(state.view)} view of ${st.name}, ${state.original?'original sample':'your selected colours'}`);
        status.textContent=fully()?'An example of a Fully Custom pair. Yours is designed from your brief.':state.original?'Original photographed sample':'Your colours on the photographed sample';
        if(!fully()) VA_PHOTO.prefetch(state.style,state.view==='front'?'back':'front',state.colours.stripePlacement);
      }).catch(error=>{
        if(request!==renderRequest) return;
        console.error('Preview failed',error); canvas.hidden=true;stage.setAttribute('aria-busy','false');
        const retry=document.createElement('button');retry.type='button';retry.textContent='Retry preview';retry.addEventListener('click',paint);
        status.textContent='The photograph could not load. Your selections are still here. ';status.append(retry);
      });
  }

  /* --- saving the design ------------------------------------------------- */
  // Both sides on one sheet with the colours named underneath, so what the
  // customer sends us and what we make the mockup from are the same thing.
  async function saveDesign() {
    if (fully()) return;
    const status = $('[data-preview-status]'), save = $('[data-save]');
    save.disabled = true;
    status.textContent = 'Preparing your front and back design…';
    const snap = {
      style: state.style,
      colours: structuredClone(state.colours),
      laces: state.laces,
      original: false,
    };
    const list = areas();
    try {
      const front = document.createElement('canvas'), back = document.createElement('canvas');
      await Promise.all([
        VA_PHOTO.render(front, { ...snap, view: 'front' }),
        VA_PHOTO.render(back, { ...snap, view: 'back' }),
      ]);
      const rows = Math.ceil((list.length + 1) / 4);
      const out = document.createElement('canvas');
      out.width = 2000; out.height = 1220 + rows * 65;
      const x = out.getContext('2d');
      x.fillStyle = '#eeefed'; x.fillRect(0, 0, out.width, out.height);
      x.fillStyle = '#161618';
      x.font = '600 46px Oswald, sans-serif';
      x.fillText('VISION ATTIRE / ' + styleById(snap.style).name.toUpperCase(), 64, 78);
      x.font = '24px Inter, sans-serif';
      x.fillText('CUSTOM DESIGN', 1450, 73);
      x.drawImage(front, 30, 120, 950, 950);
      x.drawImage(back, 1020, 120, 950, 950);
      x.font = '22px Inter, sans-serif';
      x.fillText('FRONT', 64, 1100); x.fillText('BACK', 1054, 1100);
      const settings = [['Logo', snap.colours.logoStyle === 'solid' ? 'Solid' : 'Outlined']];
      if (snap.style === 'stripe') settings.push(['Stripe', snap.colours.stripePlacement === 'right' ? 'Right leg' : 'Left leg']);
      [...list.map(([id, label]) => [label, swatchName(id === 'laces' ? snap.laces : VA_PARTS.get(snap.style, snap.colours, id), id), id]), ...settings].forEach(([label, value, id], i) => {
        const cx = 64 + (i % 4) * 485, cy = 1150 + Math.floor(i / 4) * 65;
        if (id) {
          const hex = id === 'laces' ? snap.laces : VA_PARTS.get(snap.style, snap.colours, id);
          x.fillStyle = panelsFade(id) ? (() => { const g = x.createLinearGradient(cx, cy, cx, cy + 24); VA_OMBRE.stops(snap.colours).forEach((h, k, a) => g.addColorStop(k / (a.length - 1), h)); return g; })() : hex;
          x.fillRect(cx, cy, 24, 24);
        }
        x.fillStyle = '#161618'; x.font = '19px Inter, sans-serif';
        x.fillText(label + ': ' + value, cx + (id ? 36 : 0), cy + 20);
      });
      x.font = '18px Inter, sans-serif';
      x.fillText('Screen colours are approximate. Fabric colours and the final design are confirmed on the approval mockup.',
                 64, out.height - 30);
      const blob = await new Promise(r => out.toBlob(r, 'image/png'));
      if (!blob) throw new Error('Image export failed');
      const url = URL.createObjectURL(blob), a = document.createElement('a');
      a.href = url;
      a.download = 'vision-attire-' + snap.style.replace('doubleV', 'double-v') + '-design.png';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      status.textContent = 'Design image saved with front and back views.';
    } catch (error) {
      console.error('Design export failed', error);
      status.textContent = 'Could not save the image. Please try again.';
    } finally {
      save.disabled = false;
    }
  }

  function paintChrome() {
    if (transfer) transfer.save(designPayload());
    if (fully()) {
      $('[data-price]').textContent = 'From ' + money(price().fullyCustomFrom);
      $('[data-depline]').textContent = 'Quoted after review';
      const colours = state.preferred.map(h => swatchName(h, 'fc'));
      $('[data-design-summary]').textContent = 'Fully Custom' + (colours.length ? ' · Preferred colours: ' + colours.join(', ') : '')
        + (state.chartReference.trim() ? ' · Chart reference: ' + state.chartReference.trim() : '');
      $('[data-summary]').innerHTML = `
        <div class="summary__line"><span>Fully Custom physique shorts</span><b>From ${money(price().fullyCustomFrom)}</b></div>
        <p class="hint mt-1">The final price depends on your artwork, pattern and design. We review your brief and send you a quote before anything is made.</p>`;
      $('[data-submit-note]').textContent = 'You approve your design before anything is made. ' + S.deposit.refundPolicy;
      return;
    }
    const st = styleById(state.style);
    const L = lines();
    const total = L.reduce((a, l) => a + l.amount, 0);
    const dep = total * S.deposit.split;

    $('[data-price]').textContent = money(total);
    $('[data-depline]').textContent = `50% deposit: ${money(dep)}`;
    const extra = [state.colours.logoStyle === 'solid' ? 'Logo: Solid' : 'Logo: Outlined'];
    if (state.style === 'stripe') extra.push('Stripe: ' + (state.colours.stripePlacement === 'right' ? 'Right leg' : 'Left leg'));
    $('[data-design-summary]').textContent = st.name + ' · ' + areas().map(([id, label]) => label + ': ' + swatchName(areaColour(id), id)).concat(extra).join(' · ');
    $('[data-submit-note]').textContent = 'You approve a mockup before anything is made. ' + S.deposit.refundPolicy;

    $('[data-summary]').innerHTML = `
      ${L.map(l => `<div class="summary__line"><span>${esc(l.label)}</span><b>${money(l.amount)}</b></div>`).join('')}
      <div class="summary__total"><span>Total</span><b>${money(total)}</b></div>
      <div class="summary__split">
        <div><span class="now">${esc(S.deposit.atCheckoutLabel)}</span><span class="now">${money(dep)}</span></div>
        <div><span class="later">${esc(S.deposit.balanceLabel)}</span><span class="later">${money(total - dep)}</span></div>
      </div>
      <p class="hint mt-1">This preview does not take payment. Final pricing and payment arrangements are confirmed with your design.</p>`;
  }
})();
